import type { WireProject, WireProjectDetail, WireProjectGrant, WireProjectToolGrant, WirePublication, WireRoomMessage } from '@/api/wire';
import { toolName, toolPurpose } from '@/app/technical';
import { agoWords } from '@/requests/explain';
import { actorLabel } from '@/today/copy';
import type { ItemContext, Party } from '@/ui/context';
import { LEVEL_SHORT } from '@/ui/meter';
import { twinLabel } from '@/ui/people';
import type { Who } from '@/ui/who';
import { CHANNEL_LABEL, VOICE_LEVEL, outcomeChip } from './copy';

/**
 * What a project, and what is in it, say about themselves (ADR-0155;
 * ADR-0056, ADR-0059, Amendments 0008 and 0009). A project is a room its
 * participants share and nothing outside it: the builders only restate what
 * the project's own screens already show to its people. A twin drafts in its
 * person's voice only where the project granted it, only that person decides
 * a draft, and nothing here posts; "posted" is a person saying they did.
 */

const short = (iso: string | null | undefined): string | undefined =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : undefined;

const plural = (n: number, one: string, many: string = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

export interface ProjectReader {
  /** The reader's own id, to tell their drafts and their ownership from anyone's. */
  humanId: string;
  /** Whether the reader is a participant. The page says it; the list does not. */
  member?: boolean | undefined;
  /** The reader's twin, and whether the project granted it. */
  myTwin?: { granted: boolean } | null | undefined;
}

const STATE_LINE: Record<WireProject['state'], string> = {
  active: 'It is active.',
  paused: 'It is paused, so nothing moves in it.',
  done: 'It is finished.',
  cancelled: 'It was cancelled.',
};

const isDetail = (p: WireProject | WireProjectDetail): p is WireProjectDetail => 'memberList' in p;

export function explainProject(p: WireProject | WireProjectDetail, who: Who, reader: ProjectReader): ItemContext {
  const path = `/projects/${p.id}`;
  const led = p.department ? `led by ${who.label(p.department)}` : 'across departments';
  const ownerParty: Party | null = p.owner ? (p.owner.id === reader.humanId ? { name: 'You', kind: 'person' } : { name: p.owner.displayName, kind: 'person' }) : null;
  const iOwn = p.owner?.id === reader.humanId;
  const detail = isDetail(p) ? p : null;
  const pending = detail ? detail.publications.filter((x) => x.outcome === 'pending') : [];
  const mine = pending.filter((x) => x.forHuman === reader.humanId).length;
  const others = pending.length - mine;
  const description = p.description.trim();

  let tip: string;
  let instead: string | undefined;
  let need: string | undefined;
  const open = p.state === 'active';
  if (!open) {
    tip = p.state === 'paused' ? 'Nothing is needed from you. It is paused, so nothing is drafted or said in it until it is active again.' : 'Nothing to do. Its room and its drafts stay as the record.';
    instead = p.state === 'paused' ? 'Open it to read what happened so far.' : undefined;
  } else if (detail) {
    if (mine > 0) {
      need = `Your decision on ${plural(mine, 'draft')} in your voice.`;
      tip = `${plural(mine, 'draft')} in your voice ${mine === 1 ? 'waits' : 'wait'} for you below. Read each, post it yourself where it belongs, then record whether it went out as drafted or with your edits — or discard it.`;
      instead = 'Every decision is counted toward how much this project’s twin may publish unread. Nothing is posted by the platform.';
    } else if (reader.member && reader.myTwin?.granted) {
      tip = 'Nothing waits for you. When you want the next piece, ask your twin for a draft below: it writes from what the project is for and what you posted, waits for you, and never posts.';
      instead = others > 0 ? `${plural(others, 'draft')} wait${others === 1 ? 's' : ''} for their owners, not you.` : undefined;
    } else if (reader.member && reader.myTwin) {
      tip = 'Nothing is needed from you. Your twin holds no assignment here, so it drafts nothing for this project.';
      instead = 'A superadmin can give it one.';
    } else {
      tip = 'Nothing is needed from you. The room below is where the team talks the work through.';
      instead = who.superadmin ? 'As a superadmin you can add people, lend a tool, give a twin the project and move its level from this page.' : undefined;
    }
  } else if (p.pending > 0) {
    need = `${plural(p.pending, 'draft')} waiting for the person whose voice each is in.`;
    tip = `${plural(p.pending, 'draft')} ${p.pending === 1 ? 'is' : 'are'} waiting in this project, each for the person whose voice it is in. Open it to see whether any are yours.`;
    instead = 'If none are yours, nothing is needed from you.';
  } else {
    tip = 'Nothing is waiting in this project. Open it to read the room, see who may draft, and — if you are on it — ask your twin for the next piece.';
    instead = 'Nothing happens if you leave it.';
  }

  const involved: Party[] = detail
    ? [
        ...detail.memberList
          .filter((m) => m.humanId !== reader.humanId)
          .slice(0, 6)
          .map((m): Party => ({ name: m.displayName, kind: 'person', role: m.role === 'owner' ? 'owns it' : m.role })),
        ...detail.agentMembers.slice(0, 6).map((a): Party => ({ name: `${a.givenName} Agent`, kind: 'agent', role: a.department ? who.label(a.department) : undefined })),
      ]
    : [];

  const lent = detail ? detail.toolGrants.map((g) => toolName(g.toolId)) : null;
  const mayDraft = detail ? detail.grants.map((g) => (g.principalName ? twinLabel(g.agentName, g.principalName) : g.agentName)) : null;

  return {
    subject: { kind: 'project', title: p.title },
    summary: `${p.title} is a project ${led}${p.owner ? `, owned by ${ownerParty!.name === 'You' ? 'you' : ownerParty!.name}` : ''}${description ? `: ${description.length > 220 ? `${description.slice(0, 220)}…` : description}` : '.'} ${STATE_LINE[p.state]}`,
    ...(need ? { need } : {}),
    why: iOwn
      ? 'You own it, so you decide who is on it.'
      : reader.member
        ? 'You are one of its participants.'
        : p.department
          ? `It is ${who.label(p.department)}’s project, so it is listed for the department.`
          : 'It is listed for you as a project across departments.',
    source: { label: p.department ? `Opened in ${who.label(p.department)}` : 'Opened across departments', when: short(p.createdAt) },
    broughtBy: ownerParty ? { ...ownerParty, ...(short(p.createdAt) ? { when: short(p.createdAt)! } : {}) } : null,
    owner: ownerParty,
    involved,
    knows: [...(ownerParty && ownerParty.name !== 'You' ? [ownerParty] : []), ...who.knowers(p.department)],
    facts: [
      ['People', plural(p.members, 'participant')],
      ['Drafts waiting', p.pending > 0 ? String(p.pending) : 'None'],
      ['Voice', `${LEVEL_SHORT[p.level]}: ${VOICE_LEVEL[p.level].split('—')[1]?.trim() ?? VOICE_LEVEL[p.level]}`],
      ['Tools it lends', lent ? (lent.length > 0 ? lent.join(', ') : 'None') : null],
      ['Twins that may draft', mayDraft ? (mayDraft.length > 0 ? mayDraft.join(', ') : 'None') : null],
      ['Opened', `${short(p.createdAt) ?? '—'} (${agoWords(p.createdAt) ?? 'recently'})`],
    ],
    next: { tip, ...(instead ? { instead } : {}) },
    open: { to: path, label: 'Open the project' },
  };
}

/** A tool the project lends everyone on it, for as long as the project runs (Amendment 0008). */
export function explainLentTool(g: WireProjectToolGrant, project: Pick<WireProjectDetail, 'title'>, who: Who): ItemContext {
  const name = toolName(g.toolId);
  const purpose = toolPurpose(g.toolId);
  const until = g.validTo ? `until ${short(g.validTo)}` : 'for as long as the project runs';
  const by = who.actor(g.grantedBy);
  return {
    subject: { kind: 'tool', title: name },
    summary: `${name} is lent to everyone on ${project.title}, ${until}.${purpose ? ` ${purpose}` : ''}`,
    why: 'A project may lend one of its own tools to people from any department, so they can use it inside the project. A department’s own never-list still wins: a tool it forbids is lent to nobody from it.',
    source: { label: 'A superadmin lent it to this project', when: short(g.validFrom) },
    broughtBy: by ? { ...by, ...(short(g.validFrom) ? { when: short(g.validFrom)! } : {}) } : null,
    owner: by,
    facts: [['Ends', g.validTo ? short(g.validTo)! : 'With the project']],
    next: who.superadmin
      ? { tip: 'Nothing is needed. Take it back from this list if the project no longer needs it; that spends no code.', instead: 'It ends by itself when the project closes or its end date comes.' }
      : { tip: `Nothing is needed from you. You can use it inside ${project.title}; it ends with the project.`, instead: 'To have it taken back, or another lent, ask a superadmin.' },
  };
}

/** A twin the project let draft in its person's voice (§15), and until when. */
export function explainTwinGrant(g: WireProjectGrant, project: Pick<WireProjectDetail, 'title'>): ItemContext {
  const name = g.principalName ? twinLabel(g.agentName, g.principalName) : `${g.agentName} · a twin`;
  const person = g.principalName ?? 'its person';
  const tools = g.tools.map(toolName);
  return {
    subject: { kind: 'twin', title: name },
    summary: `${name} may draft in ${project.title}, in ${person}’s voice${g.validTo ? `, until ${short(g.validTo)}` : ''}. It reaches nothing outside this project.`,
    why: 'A twin drafts in its person’s voice only inside a project whose assignment it holds. Only that person decides what it writes, and nothing is posted by the platform.',
    source: { label: g.title, when: short(g.validFrom) },
    broughtBy: null,
    owner: g.principalName ? { name: g.principalName, kind: 'person', role: 'decides every draft' } : null,
    involved: [{ name, kind: 'twin' }],
    knows: g.principalName ? [{ name: g.principalName, kind: 'person' }] : [],
    facts: [
      ['Tools it may use', tools.length > 0 ? tools.join(', ') : null],
      ['Ends', g.validTo ? short(g.validTo)! : 'With the project'],
    ],
    next: { tip: `Nothing is needed. It drafts only when ${person} asks it to, and every piece waits for ${person} to read and decide.`, instead: 'The grant ends when the project closes, when the person leaves it, or on its end date.' },
  };
}

/** A draft in a person's voice for a channel: waiting for them, or decided. */
export function explainPublication(p: WirePublication, project: Pick<WireProjectDetail, 'title'>, who: Who, reader: { humanId: string }): ItemContext {
  const mine = p.forHuman === reader.humanId;
  const voice = mine ? 'your voice' : `${p.forName}’s voice`;
  const channel = CHANNEL_LABEL[p.channel];
  const twin = p.draftedBy.endsWith('.twin');
  const label = p.draftedBy.includes('@') && !p.draftedBy.includes('.agent@') ? (who.actor(p.draftedBy)?.name ?? 'Somebody') : actorLabel(p.draftedBy, p.forName);
  const preparer: Party = { name: label, kind: twin ? 'twin' : 'agent' };
  const title = p.title ?? channel;
  const chip = outcomeChip(p.outcome);
  const owner: Party = mine ? { name: 'You', kind: 'person', role: 'decides it' } : { name: p.forName, kind: 'person', role: 'decides it' };
  const pending = p.outcome === 'pending';

  let tip: string;
  let instead: string | undefined;
  if (pending && mine) {
    tip = 'Read it and change what is off. Post it yourself where it belongs, then record whether it went out as drafted or with your edits — or discard it.';
    instead = 'Every decision is counted toward how much this project’s twin may publish unread. Nothing is posted by the platform.';
  } else if (pending) {
    tip = `Nothing is needed from you. It is in ${p.forName}’s voice, so it is theirs to read and decide.`;
  } else if (p.outcome === 'posted_unchanged') {
    tip = 'Nothing to do. It was posted as drafted, which counts toward the project’s level.';
  } else if (p.outcome === 'posted_edited') {
    tip = `Nothing to do. It was posted with edits${p.editDistance !== null ? ` (${plural(p.editDistance, 'character')} changed)` : ''}, which counts toward the project’s level.`;
  } else {
    tip = 'Nothing to do. It was discarded and counted as one that did not go out.';
  }

  return {
    subject: { kind: 'draft', title },
    summary: `${channel}${p.title ? ` “${p.title}”` : ''} written by ${label} in ${voice} for ${project.title}. ${pending ? 'It has not been posted.' : `${chip.label.charAt(0)}${chip.label.slice(1).toLowerCase()}.`}`,
    ...(pending && mine ? { need: 'Your decision: record it as posted as drafted, posted with your edits, or discarded.' } : {}),
    why: mine ? 'It is in your voice, so only you decide it. A twin drafts here only because the project granted it, and the platform posts nothing.' : `It is in ${p.forName}’s voice, so only they decide it.`,
    source: { label: `A ${channel.toLowerCase()} draft asked for in ${project.title}`, when: short(p.createdAt) },
    broughtBy: { ...preparer, ...(short(p.createdAt) ? { when: short(p.createdAt)! } : {}) },
    owner,
    knows: [],
    facts: [
      ['Where it would go', channel],
      ['Prepared', agoWords(p.createdAt) ?? null],
      ['Decided', short(p.decidedAt) ?? null],
      ['What you changed', p.outcome === 'posted_edited' && p.editDistance !== null ? plural(p.editDistance, 'character') : null],
    ],
    next: { tip, ...(instead ? { instead } : {}) },
  };
}

/** An action from a meeting's minutes: who it is for, and where it was filed. */
export function explainRoomAction(m: WireRoomMessage, project: Pick<WireProjectDetail, 'title'>, who: Who): ItemContext {
  const owner: Party | null = m.actionFor ? { name: m.actionFor.name, kind: 'person', ...(m.actionFor.department ? { role: who.label(m.actionFor.department) } : {}) } : null;
  const author: Party = m.author.kind === 'agent' ? { name: m.author.name, kind: 'agent', ...(m.author.department ? { role: who.label(m.author.department) } : {}) } : { name: m.author.name, kind: 'person' };
  const whom = m.actionFor?.name ?? 'somebody on the project';
  const filed = m.requestId
    ? `It went out as a request to ${m.actionFor?.department ? who.label(m.actionFor.department) : 'another department'}.`
    : m.taskId
      ? 'It is a task in the department that wrote the minutes.'
      : 'It was filed nowhere.';
  return {
    subject: { kind: 'action', title: `An action for ${whom}` },
    summary: `An action from the minutes of a meeting on ${project.title}, for ${whom}. ${filed}`,
    need: m.requestId || m.taskId ? `${whom} to do it.` : `Somebody to take it up: nothing was filed for it.`,
    why: 'A meeting’s minutes name an owner among the participants, and an action for another department goes out as a request, never as a row written into it.',
    source: { label: `The minutes of a meeting on ${project.title}`, when: short(m.at) },
    broughtBy: { ...author, ...(short(m.at) ? { when: short(m.at)! } : {}) },
    owner,
    next: m.requestId
      ? { tip: `Nothing is needed from the room. ${whom}’s department has the request and answers it there.`, instead: 'If it should be answered sooner, raise it in the room.' }
      : m.taskId
        ? { tip: 'Nothing is needed from the room. It is a task, and the department’s own people or agents work it.', instead: 'If it matters more than that, raise it in the room.' }
        : { tip: `Nothing was filed for this one, so nobody is working on it yet. Tell ${whom}, or raise it in the room.`, instead: 'If it is no longer needed, it can stay as it is; it blocks nothing.' },
  };
}
